/**
 * Authentication utilities for admin portal
 */

interface AdminUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_super_admin: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_login?: string | null;
  // Computed property for display
  full_name?: string;
}

interface LoginResponse {
  access_token: string;
  token_type: string;
  admin_id: number;
  email: string;
  full_name: string;
  role: string;
  is_super_admin: boolean;
}

class AuthService {
  private static instance: AuthService;
  private token: string | null = null;
  private adminUser: AdminUser | null = null;
  private tokenCheckInterval: NodeJS.Timeout | null = null;

  private constructor() {
    this.token = localStorage.getItem('admin_token');
    const adminUserStr = localStorage.getItem('admin_user');
    if (adminUserStr) {
      try {
        this.adminUser = JSON.parse(adminUserStr);
      } catch (e) {
        console.error('Error parsing admin user from localStorage:', e);
        this.clearAuth();
      }
    }
    
    // Start token expiration checking
    this.startTokenExpirationCheck();
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Login with email and password
   */
  public async login(email: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data: LoginResponse = await response.json();
        
        // Store token and user info
        this.token = data.access_token;
        
        // Parse first_name and last_name from full_name if available
        const nameParts = data.full_name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        this.adminUser = {
          id: data.admin_id,
          email: data.email,
          first_name: firstName,
          last_name: lastName,
          role: data.role,
          is_super_admin: data.is_super_admin,
          is_active: true, // Assume active if logged in successfully
          created_at: new Date().toISOString(), // Current time as fallback
          updated_at: new Date().toISOString(), // Current time as fallback
          last_login: null,
          full_name: data.full_name
        };

        localStorage.setItem('admin_token', this.token);
        localStorage.setItem('admin_user', JSON.stringify(this.adminUser));

        // Start token expiration checking
        this.startTokenExpirationCheck();

        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || 'Login failed' };
      }
    } catch (err) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  /**
   * Logout and clear all auth data
   */
  public logout(): void {
    this.clearAuth();
    // Redirect to login page
    window.location.href = '/admin/login';
  }

  /**
   * Clear all authentication data
   */
  private clearAuth(): void {
    this.token = null;
    this.adminUser = null;
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    
    // Stop token checking
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    return !!this.token && !!this.adminUser && this.isTokenValid();
  }

  /**
   * Get current admin user
   */
  public getCurrentUser(): AdminUser | null {
    return this.adminUser;
  }

  /**
   * Get auth token
   */
  public getToken(): string | null {
    return this.token;
  }

  /**
   * Get authorization header
   */
  public getAuthHeader(): { Authorization: string } | {} {
    if (this.token) {
      return { Authorization: `Bearer ${this.token}` };
    }
    return {};
  }

  /**
   * Check if token is still valid (not expired)
   */
  private isTokenValid(): boolean {
    if (!this.token) return false;

    try {
      // Decode JWT payload (base64 decode the middle part)
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      // Check if token is expired
      return payload.exp && payload.exp > currentTime;
    } catch (e) {
      console.error('Error decoding token:', e);
      return false;
    }
  }

  /**
   * Start checking token expiration every minute
   */
  private startTokenExpirationCheck(): void {
    // Clear existing interval
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }

    // Check token validity every minute
    this.tokenCheckInterval = setInterval(() => {
      if (this.token && !this.isTokenValid()) {
        console.warn('Token expired, logging out...');
        this.handleTokenExpiration();
      }
    }, 60000); // Check every minute
  }

  /**
   * Handle token expiration
   */
  private handleTokenExpiration(): void {
    // Clear auth data
    this.clearAuth();
    
    // Show expiration message
    alert('Your session has expired. Please login again.');
    
    // Redirect to login
    window.location.href = '/admin/login';
  }

  /**
   * Make authenticated API request
   */
  public async apiRequest(url: string, options: RequestInit = {}): Promise<Response> {
    // Check if token is valid before making request
    if (!this.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const headers = {
      'Content-Type': 'application/json',
      ...this.getAuthHeader(),
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 responses (token expired/invalid)
    if (response.status === 401) {
      this.handleTokenExpiration();
      throw new Error('Authentication expired');
    }

    return response;
  }
}

// Export singleton instance
export const authService = AuthService.getInstance();

// Export types
export type { AdminUser, LoginResponse };